/**
 * 1.5.0 shot detection: the data hooks (docs/dev/shots-1.5.0-build.md). Step 0 wrote every hook against the routes
 * (S1 builds them; until then they answer 501); S2 owns this file now and fixes a hook when its route turns out
 * different.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SHOTS_STRATUM_ID, TICKS_PER_SECOND,
  type DetectShotsRequest, type DetectShotsResult, type RemoveEmptySegmentsResult, type SegmentDTO,
  type SegmentsFromShotsRequest, type SegmentsFromShotsResult, type ShotDetectionSettings, type ShotDetectionSettingsDTO,
} from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { useSegments } from './assets';

/** The workspace's settings (everyone may read them: the Detect shots dialog prefills from them). */
export function useShotSettings(enabled = true) {
  return useQuery({ queryKey: queryKeys.shotSettings(), queryFn: () => api<ShotDetectionSettingsDTO>('/api/v1/settings/shot-detection'), enabled, staleTime: 60_000 });
}

/** Administration → Transcoding → Shots and scrub images (admin). */
export function useSaveShotSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShotDetectionSettings) => api<ShotDetectionSettingsDTO>('/api/v1/settings/shot-detection', { method: 'PUT', json: body }),
    onSuccess: (data) => qc.setQueryData(queryKeys.shotSettings(), data),
  });
}

/** Detect shots on one asset (processing.retry), with optional overrides. */
export function useDetectShots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, overrides = {} }: { assetId: string; overrides?: DetectShotsRequest }) =>
      api<DetectShotsResult>(`/api/v1/assets/${assetId}/detect-shots`, { method: 'POST', json: overrides }),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: queryKeys.asset(v.assetId) }); },
  });
}

/** Fill a log layer from the shots (asset.edit_metadata). */
export function useFillFromShots(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SegmentsFromShotsRequest) => api<SegmentsFromShotsResult>(`/api/v1/assets/${assetId}/segments/from-shots`, { method: 'POST', json: body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.segments(assetId) }); },
  });
}

/** Remove a log layer's empty segments (asset.edit_metadata). */
export function useRemoveEmptySegments(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stratumId: string) => api<RemoveEmptySegmentsResult>(`/api/v1/assets/${assetId}/segments/remove-empty`, { method: 'POST', json: { stratumId } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.segments(assetId) }); },
  });
}

/** The Shots layer's segments of the asset's current version, in order (empty: no shots detected). */
export function shotSegments(items: SegmentDTO[] | undefined): SegmentDTO[] {
  return (items ?? []).filter((s) => s.stratumId === SHOTS_STRATUM_ID).sort((a, b) => a.startTicks - b.startTicks);
}

/** The shots' starts in seconds (the player's ticks and jumps); null while loading or without shots. */
export function useShotStarts(assetId: string | undefined): number[] | null {
  const segs = useSegments(assetId);
  return useMemo(() => {
    const shots = shotSegments(segs.data?.items);
    return shots.length ? shots.map((s) => s.startTicks / TICKS_PER_SECOND) : null;
  }, [segs.data]);
}
